/**
 * Centralized Routes Configuration
 * All routing paths are defined here, outside of the pages folder
 * This configuration is used by ComponentRegistry and routing system
 */

// Import all components from pages (components are still in pages, but routes are centralized here)
import AdminDashboard from '../pages/admin/AdminDashboard';
import AdminTicketsList from '../pages/admin/AdminTicketsList';
import AdminRegister from '../pages/admin/Register';
import CategoryManagement from '../pages/admin/CategoryManagement';
import UserManagement from '../pages/admin/UserManagement';
import RoleManagement from '../pages/admin/RoleManagement';
import EmployeeHistory from '../pages/admin/EmployeeHistory';
import SlaBreachedReport from '../pages/admin/SlaBreachedReport';
import ListOfTicketsReport from '../pages/admin/ListOfTicketsReport';
import AgentsReport from '../pages/admin/AgentsReport';
import SourceWiseTicketsReport from '../pages/admin/SourceWiseTicketsReport';
import AgentWiseSLABreachReport from '../pages/admin/AgentWiseSLABreachReport';
import AttendanceView from '../pages/admin/AttendanceView';
import UnifiedSLAManagement from '../pages/admin/UnifiedSLAManagement';
import EscalatedTicketsManagement from '../pages/escalated';
import UnifiedWorkflow from '../pages/admin/UnifiedWorkflow';
import AdministrationContainer from '../modules/administration/AdministrationContainer';
import UserGuide from '../components/UserGuide';
// Chatbot (full-route pages; floating widget also on layouts)
import LandingChatbot from '../components/chatbot/LandingChatbot';

// Agent Components
import AgentDashboard from '../pages/agent/AgentDashboard';
import UnassignedTickets from '../pages/agent/tickets/UnassignedTickets';
import AgentMyTickets from '../pages/agent/tickets/MyTickets';
import TeamTickets from '../pages/agent/tickets/TeamTickets';
import EscalatedTickets from '../pages/agent/tickets/EscalatedTickets';
import ClosedTickets from '../pages/agent/tickets/ClosedTickets';
import MyArticlesScreen from '../pages/agent/knowledge/MyArticlesScreen';
import UserGuideScreen from '../pages/agent/knowledge/UserGuideScreen';
import ReferenceCustomerLogin from '../pages/agent/integratedmodule/ReferenceCustomerLogin';
import MyTicketsOverview from '../pages/agent/reports/MyTicketsOverview';
import AgentTicketDetail from '../pages/agent/tickets/TicketDetail';
import TicketDetail from '../pages/agent/tickets/TicketDetail';

// Portal Components
import UnifiedPortalDashboard from '../pages/portal/UnifiedPortalDashboard';
import UnifiedKnowledgeBase from '../pages/portal/UnifiedKnowledgeBase';
import UnifiedProfile from '../pages/portal/UnifiedProfile';
import MyTickets from '../pages/portal/tickets/MyTickets';
import CreateTicketForm from '../pages/portal/tickets/CreateTicketForm';

// Super Admin Components
import SuperAdminDashboard from '../pages/super-admin/SuperAdminDashboard';
import OrganizationalManagement from '../pages/super-admin/OrganizationalManagement';
import CreateTenant from '../pages/super-admin/CreateTenant';
import GlobalSettings from '../pages/super-admin/GlobalSettings';
import SuperAdminRegister from '../pages/super-admin/Register';
import SystemMonitoring from '../pages/super-admin/SystemMonitoring';
import LicenseManagement from '../pages/super-admin/LicenseManagement';
import EmergencyActions from '../pages/super-admin/EmergencyActions';
import UnifiedBilling from '../pages/super-admin/UnifiedBilling';
import AIConfigurationDashboard from '../pages/super-admin/ai/AIConfigurationDashboard';
import AIPerformanceMonitoring from '../pages/super-admin/ai/AIPerformanceMonitoring';
import ModelManagement from '../pages/super-admin/ai/ModelManagement';
import AutomationRules from '../pages/super-admin/ai/AutomationRules';
import AIAnalytics from '../pages/super-admin/ai/AIAnalytics';
import AISettings from '../pages/super-admin/ai/AISettings';
import AuditorDashboard from '../pages/super-admin/audit/AuditorDashboard';
import ComplianceReporting from '../pages/super-admin/audit/ComplianceReporting';
import ComplianceReports from '../pages/super-admin/audit/ComplianceReports';
import AuditFindings from '../pages/super-admin/audit/AuditFindings';
import AuditFindingsManagement from '../pages/super-admin/audit/AuditFindingsManagement';
import SuperAdminProfileManagement from '../pages/super-admin/profile/SuperAdminProfileManagement';
import TenantPermissions from '../pages/super-admin/TenantPermissions';
import MasterFeaturesManagement from '../pages/super-admin/MasterFeaturesManagement';

// Additional Admin Components (for comprehensive route coverage)
import AllUsers from '../pages/admin/AllUsers';
import RoutingRulesConfiguration from '../pages/admin/RoutingRulesConfiguration';
import AutoAssignmentDashboard from '../pages/admin/AutoAssignmentDashboard';
import FeedbackAnalytics from '../pages/admin/FeedbackAnalytics';
import SystemConfigurationManagement from '../pages/admin/SystemConfigurationManagement';
import EmailDiagnostics from '../pages/admin/EmailDiagnostics';
import EscalationRules from '../pages/admin/EscalationRules';
import AgentsManagement from '../pages/admin/AgentsManagement';
import PermissionManagement from '../pages/admin/PermissionManagement';
import DepartmentManagement from '../pages/admin/DepartmentManagement';
import UserProfileManagement from '../pages/admin/UserProfileManagement';
import AdminProfileManagement from '../pages/admin/profile/AdminProfileManagement';
import PluginStore from '../pages/admin/PluginStore';
import InstalledPlugins from '../pages/admin/InstalledPlugins';
import CustomHooks from '../pages/admin/CustomHooks';
import ComplianceOverview from '../pages/admin/ComplianceOverview';
import RiskHeatmap from '../pages/admin/RiskHeatmap';
import TelephonyConfiguration from '../pages/admin/TelephonyConfiguration';
import SMSConfiguration from '../pages/admin/SMSConfiguration';
import APIKeysManagement from '../pages/admin/APIKeysManagement';
import WebhooksManagement from '../pages/admin/WebhooksManagement';
import ThirdPartyApps from '../pages/admin/ThirdPartyApps';
import TicketCategories from '../pages/admin/TicketCategories';
import SystemIntegration from '../pages/admin/SystemIntegration';
import SystemPerformance from '../pages/admin/SystemPerformance';
import TicketTypesManagement from '../pages/admin/TicketTypesManagement';
import PrioritiesManagement from '../pages/admin/PrioritiesManagement';
import CustomFieldsManagement from '../pages/admin/CustomFieldsManagement';
import TicketStatusManagement from '../pages/admin/TicketStatusManagement';
import AgentKPIs from '../pages/admin/AgentKPIs';
import DepartmentMetrics from '../pages/admin/DepartmentMetrics';
import SystemMonitoringAdmin from '../pages/admin/SystemMonitoring';
import PerformanceOptimization from '../pages/admin/PerformanceOptimization';
import ResourceManagement from '../pages/admin/ResourceManagement';
import BackupRecovery from '../pages/admin/BackupRecovery';
import QueueManagement from '../pages/admin/QueueManagement';
import SystemLogs from '../pages/admin/SystemLogs';
import SubscriptionPlans from '../pages/admin/billing/SubscriptionPlans';
import PaymentHistory from '../pages/admin/billing/PaymentHistory';
import UnifiedSettings from '../pages/admin/UnifiedSettings';
import UnifiedAnalytics from '../pages/admin/UnifiedAnalytics';
import UnifiedSecurity from '../pages/admin/UnifiedSecurity';
import UnifiedEmailManagement from '../pages/admin/UnifiedEmailManagement';
import UnifiedNotifications from '../pages/admin/UnifiedNotifications';
import Reports from '../pages/admin/Reports';
import AIConfigurationDashboardAdmin from '../pages/admin/ai/AIConfigurationDashboard';
import AIPerformanceMonitoringAdmin from '../pages/admin/ai/AIPerformanceMonitoring';
import ModelManagementAdmin from '../pages/admin/ai/ModelManagement';
import AIAutomationRules from '../pages/admin/ai/AutomationRules';
import AIAnalyticsAdmin from '../pages/admin/ai/AIAnalytics';
import AISettingsAdmin from '../pages/admin/ai/AISettings';
import AuditorDashboardAdmin from '../pages/admin/audit/AuditorDashboard';
import AuditFindingsAdmin from '../pages/admin/audit/AuditFindings';
import AuditFindingsManagementAdmin from '../pages/admin/audit/AuditFindingsManagement';
import ComplianceReportingAdmin from '../pages/admin/audit/ComplianceReporting';
import ComplianceReportsAdmin from '../pages/admin/audit/ComplianceReports';

// Additional Agent Components
import TicketQueue from '../pages/agent/tickets/TicketQueue';
import AgentCreateTicket from '../pages/agent/tickets/CreateTicket';
import AutoLoginRedirect from '../pages/agent/tickets/AutoLoginRedirect';
import TicketAssignment from '../pages/agent/TicketAssignment';
import TicketEscalation from '../pages/agent/TicketEscalation';
import TicketResolution from '../pages/agent/TicketResolution';
import UnifiedAgentKnowledgeBase from '../pages/agent/UnifiedAgentKnowledgeBase';
import AgentReports from '../pages/agent/reports/AgentReports';
import AgentPerformanceManagement from '../pages/agent/performance/AgentPerformanceManagement';
import AgentTrainingManagement from '../pages/agent/training/AgentTrainingManagement';
import AgentProfileManagement from '../pages/agent/profile/AgentProfileManagement';
import AgentSettingsManagement from '../pages/agent/settings/AgentSettingsManagement';
import AgentHelpSupport from '../pages/agent/help/AgentHelpSupport';
import AgentCollaboration from '../pages/agent/collaboration/AgentCollaboration';
import AgentMobileAccess from '../pages/agent/mobile/AgentMobileAccess';
import AgentIntegrationTools from '../pages/agent/integrations/AgentIntegrationTools';
import AgentWorkflowOptimization from '../pages/agent/workflow/AgentWorkflowOptimization';
import AgentAIAssistant from '../pages/agent/ai/AgentAIAssistant';
import AgentAdvancedAnalytics from '../pages/agent/analytics/AgentAdvancedAnalytics';
import Approvals from '../pages/agent/Approvals';
import AuditReports from '../pages/agent/audit/AuditReports';
import PlaceholderPage from '../pages/agent/PlaceholderPage';

// Portal Additional Components
import UnifiedFeedback from '../pages/portal/UnifiedFeedback';
import PortalNotFound from '../pages/portal/PortalNotFound';
import TicketCommunication from '../pages/portal/tickets/TicketCommunication';
import TicketNotifications from '../pages/portal/notifications/TicketNotifications';
import AttachFiles from '../pages/portal/files/AttachFiles';
import RateExperience from '../pages/portal/feedback/RateExperience';

// Public pages
import PrivacyPolicy from '../pages/public/PrivacyPolicy';

// Developer Components
import UnifiedDeveloperDashboard from '../pages/developer/UnifiedDeveloperDashboard';
import APIKeyManagement from '../pages/developer/APIKeyManagement';
import APIDocumentation from '../pages/developer/documentation/APIDocumentation';
import APILimits from '../pages/developer/APILimits';
import WebhookConfiguration from '../pages/developer/webhooks/WebhookConfiguration';
import ExternalSystemIntegration from '../pages/developer/ExternalSystemIntegration';
import IntegrationAnalytics from '../pages/developer/IntegrationAnalytics';
import IntegrationSettings from '../pages/developer/IntegrationSettings';
import DeveloperTools from '../pages/developer/tools/DeveloperTools';
import DeveloperProfileManagement from '../pages/developer/profile/DeveloperProfileManagement';

import { Navigate } from 'react-router-dom';

// Callcenter Pages
import CallcenterUsersPage from '../pages/callcenter/Users';
import CallcenterUserCreatePage from '../pages/callcenter/UserCreate';
import CallcenterQueuePage from '../pages/callcenter/Queue';
import CallcenterQueueStatusPage from '../pages/callcenter/QueueStatus';

/**
 * Centralized Route Map
 * Maps all routes to their components
 * Routes are organized by role prefix but defined in one place
 */
export const CENTRALIZED_ROUTE_MAP = {
  // Public / landing pages
  '/privacy': PrivacyPolicy,
  '/privacy-policy': PrivacyPolicy,

  // Callcenter Routes (global)
  '/callcenter/users': CallcenterUsersPage,
  '/callcenter/users/new': CallcenterUserCreatePage,
  '/callcenter/queue': CallcenterQueuePage,
  '/callcenter/queue/status': CallcenterQueueStatusPage,

  // Admin Routes
  '/admin': AdminDashboard,
  '/admin/dashboard': AdminDashboard,
  '/admin/tickets': AdminTicketsList,
  '/admin/tickets/:id': TicketDetail,
  '/admin/users': UserManagement,
  '/admin/users/management': UserManagement,
  '/admin/users/all': AllUsers,
  '/admin/users/register': AdminRegister,
  '/admin/users/agents': AgentsManagement,
  '/admin/users/permissions': PermissionManagement,
  '/admin/users/departments': DepartmentManagement,
  '/admin/users/categories': CategoryManagement,
  '/admin/users/profiles': UserProfileManagement,
  '/admin/profile': AdminProfileManagement,
  '/admin/reports': Reports,
  '/admin/reports/employee-history': EmployeeHistory,
  '/admin/reports/sla-breached': SlaBreachedReport,
  '/admin/reports/list-of-tickets': ListOfTicketsReport,
  '/admin/reports/agents': AgentsReport,
  '/admin/reports/source-wise-tickets': SourceWiseTicketsReport,
  '/admin/reports/agent-wise-sla-breach': AgentWiseSLABreachReport,
  '/admin/reports/attendance': AttendanceView,
  '/admin/attendance': AttendanceView,
  '/reports/attendance': AttendanceView,
  '/admin/attendance/view': AttendanceView,
  '/admin/reports/agent-kpis': AgentKPIs,
  '/admin/reports/department-metrics': DepartmentMetrics,
  '/admin/security': UnifiedSecurity,
  '/admin/security/roles': RoleManagement,
  '/admin/security/policies': UnifiedSecurity,
  '/admin/security/management': UnifiedSecurity,
  '/admin/security/login-policies': UnifiedSecurity,
  '/admin/security/login-policies-management': UnifiedSecurity,
  '/admin/security/audit-logs': UnifiedSecurity,
  '/admin/security/data-retention': UnifiedSecurity,
  '/admin/workflow': UnifiedWorkflow,
  '/admin/workflow/builder': UnifiedWorkflow,
  '/admin/workflow/automation': UnifiedWorkflow,
  '/admin/workflow/templates': UnifiedWorkflow,
  '/admin/workflow/monitoring': UnifiedWorkflow,
  '/admin/sla': UnifiedSLAManagement,
  '/admin/sla/policy-management': UnifiedSLAManagement,
  '/admin/sla/monitoring': UnifiedSLAManagement,
  '/admin/sla/audit': UnifiedSLAManagement,
  '/admin/reports/sla': UnifiedSLAManagement,
  '/admin/escalation-rules': EscalationRules,
  '/admin/manage/escalated': EscalatedTicketsManagement,
  '/admin/manage/ticket-types': TicketTypesManagement,
  '/admin/manage/priorities': PrioritiesManagement,
  '/admin/manage/custom-fields': CustomFieldsManagement,
  '/admin/manage/ticket-categories': TicketCategories,
  '/admin/settings': UnifiedSettings,
  '/admin/settings/general': UnifiedSettings,
  '/admin/settings/email': UnifiedSettings,
  '/admin/settings/security': UnifiedSettings,
  '/admin/settings/integrations': UnifiedSettings,
  '/admin/settings/cache': UnifiedSettings,
  '/admin/settings/system': UnifiedSettings,
  '/admin/settings/business-hours': UnifiedSettings,
  '/admin/settings/business-hours/profiles': UnifiedSettings,
  '/admin/settings/business-hours/management': UnifiedSettings,
  '/admin/emails': UnifiedEmailManagement,
  '/admin/email-templates': UnifiedEmailManagement,
  '/admin/email-template-management': UnifiedEmailManagement,
  '/admin/emails/diagnostics': EmailDiagnostics,
  '/admin/notify': UnifiedNotifications,
  '/admin/notify/templates': UnifiedNotifications,
  '/admin/notify/channels': UnifiedNotifications,
  '/admin/notify/rules': UnifiedNotifications,
  '/admin/analytics': UnifiedAnalytics,
  '/admin/analytics/reports': UnifiedAnalytics,
  '/admin/analytics/metrics': UnifiedAnalytics,
  '/admin/analytics/export': UnifiedAnalytics,
  '/admin/analytics/compliance': UnifiedAnalytics,
  '/admin/analytics/reporting': UnifiedAnalytics,
  '/admin/analytics/chat': UnifiedAnalytics,
  '/admin/feedback-analytics': FeedbackAnalytics,
  '/admin/integrations/telephony': TelephonyConfiguration,
  '/admin/integrations/sms': SMSConfiguration,
  '/admin/integrations/api-keys': APIKeysManagement,
  '/admin/integrations/webhooks': WebhooksManagement,
  '/admin/integrations/third-party': ThirdPartyApps,
  '/admin/integrations/system': SystemIntegration,
  '/admin/plugins/marketplace': PluginStore,
  '/admin/plugins/installed': InstalledPlugins,
  '/admin/plugins/hooks': CustomHooks,
  '/admin/governance/compliance': ComplianceOverview,
  '/admin/governance/risk': RiskHeatmap,
  '/admin/monitoring': SystemMonitoringAdmin,
  '/admin/performance': SystemPerformance,
  '/admin/performance-optimization': PerformanceOptimization,
  '/admin/resource-management': ResourceManagement,
  '/admin/backup-recovery': BackupRecovery,
  '/admin/queue': QueueManagement,
  '/admin/logs': SystemLogs,
  '/admin/maintenance': UnifiedSettings,
  '/admin/system-configuration': SystemConfigurationManagement,
  '/admin/routing': RoutingRulesConfiguration,
  '/admin/auto-assignment': AutoAssignmentDashboard,
  '/admin/billing/subscription': SubscriptionPlans,
  '/admin/billing/transactions': PaymentHistory,
  '/admin/ai': AIConfigurationDashboardAdmin,
  '/admin/ai/performance': AIPerformanceMonitoringAdmin,
  '/admin/ai/models': ModelManagementAdmin,
  '/admin/ai/automation': AIAutomationRules,
  '/admin/ai/analytics': AIAnalyticsAdmin,
  '/admin/ai/settings': AISettingsAdmin,
  '/admin/audit': AuditorDashboardAdmin,
  '/admin/audit/findings': AuditFindingsAdmin,
  '/admin/audit/findingmanagement': AuditFindingsManagementAdmin,
  '/admin/audit/compliance': ComplianceReportingAdmin,
  '/admin/audit/reports': ComplianceReportsAdmin,
  '/admin/administration/modules': AdministrationContainer,
  '/admin/administration/screens': AdministrationContainer,
  '/admin/knowledge-base/user-guide': UserGuide,

  // Agent Routes
  '/agent': AgentDashboard,
  '/agent/dashboard': AgentDashboard,
  '/agent/tickets': UnassignedTickets,
  '/agent/tickets/unassigned': UnassignedTickets,
  '/agent/tickets/my-tickets': AgentMyTickets,
  '/agent/tickets/team-tickets': TeamTickets,
  '/agent/tickets/escalated': EscalatedTickets,
  '/agent/tickets/closed': ClosedTickets,
  '/agent/tickets/new': AgentCreateTicket,
  '/agent/tickets/newest': AutoLoginRedirect,
  '/agent/tickets/assignment': TicketAssignment,
  '/agent/tickets/escalation': TicketEscalation,
  '/agent/tickets/resolution': TicketResolution,
  '/agent/tickets/:id': AgentTicketDetail,
  '/agent/knowledge': UnifiedAgentKnowledgeBase,
  '/agent/knowledge/articles': UnifiedAgentKnowledgeBase,
  '/agent/knowledge/drafts': UnifiedAgentKnowledgeBase,
  '/agent/knowledge/my-articles': MyArticlesScreen,
  '/agent/knowledge/pending-review': UnifiedAgentKnowledgeBase,
  '/agent/knowledge/published': UnifiedAgentKnowledgeBase,
  '/agent/knowledge/user-guide': UserGuideScreen,
  '/agent/integrated-module': ReferenceCustomerLogin,
  '/agent/integrated-module/reference-customer-login': ReferenceCustomerLogin,
  '/agent/reports': AgentReports,
  '/agent/reports/my-tickets-overview': MyTicketsOverview,
  '/agent/performance': AgentPerformanceManagement,
  '/agent/training': AgentTrainingManagement,
  '/agent/profile': AgentProfileManagement,
  '/agent/settings': AgentSettingsManagement,
  '/agent/help': AgentHelpSupport,
  '/agent/collaboration': AgentCollaboration,
  '/agent/mobile': AgentMobileAccess,
  '/agent/integrations': AgentIntegrationTools,
  '/agent/workflow': AgentWorkflowOptimization,
  '/agent/ai-assistant': AgentAIAssistant,
  '/agent/analytics': AgentAdvancedAnalytics,
  '/agent/approvals': Approvals,
  '/agent/audit': AuditReports,
  '/agent/audit/reports': AuditReports,
  // Agent Chatbot
  '/agent/chatbot': LandingChatbot,
  '/agent/monitoring': PlaceholderPage,
  '/agent/monitoring/metrics': PlaceholderPage,
  '/agent/monitoring/post-change-review': PlaceholderPage,
  '/agent/configuration': PlaceholderPage,
  '/agent/configuration/ci-graph': PlaceholderPage,
  '/agent/configuration/dependency-tree': PlaceholderPage,
  '/agent/configuration/impact-analysis': PlaceholderPage,

  // Super Admin Routes
  '/super-admin': SuperAdminDashboard,
  '/super-admin/dashboard': SuperAdminDashboard,
  '/super-admin/organization': OrganizationalManagement,
  '/organizationalmanagement/tenantcreation': CreateTenant,
  '/super-admin/tenants/:tenantId/permissions': TenantPermissions,
  '/org/masterfeatures': MasterFeaturesManagement,
  '/organizationalmanagement/masterfeatures': MasterFeaturesManagement,
  '/super-admin/register': SuperAdminRegister,
  '/super-admin/settings': GlobalSettings,
  '/super-admin/monitoring': SystemMonitoring,
  '/super-admin/license': LicenseManagement,
  '/super-admin/emergency': EmergencyActions,
  '/super-admin/billing': UnifiedBilling,
  '/super-admin/billing/subscriptions': UnifiedBilling,
  '/super-admin/billing/payments': UnifiedBilling,
  '/super-admin/billing/invoices': UnifiedBilling,
  '/super-admin/billing/reports': UnifiedBilling,
  '/super-admin/billing/settings': UnifiedBilling,
  '/super-admin/ai': AIConfigurationDashboard,
  '/super-admin/ai/performance': AIPerformanceMonitoring,
  '/super-admin/ai/models': ModelManagement,
  '/super-admin/ai/automation': AutomationRules,
  '/super-admin/ai/analytics': AIAnalytics,
  '/super-admin/ai/settings': AISettings,
  '/super-admin/audit': AuditorDashboard,
  '/super-admin/audit/compliance': ComplianceReporting,
  '/super-admin/audit/findings': AuditFindings,
  '/super-admin/audit/findingmanagement': AuditFindingsManagement,
  '/super-admin/audit/reports': ComplianceReports,
  '/super-admin/profile': SuperAdminProfileManagement,

  // Alternative Super Admin Routes (without prefix for dynamic menu compatibility)
  '/ai': AIConfigurationDashboard,
  '/ai/performance': AIPerformanceMonitoring,
  '/ai/models': ModelManagement,
  '/ai/automation': AutomationRules,
  '/ai/analytics': AIAnalytics,
  '/ai/settings': AISettings,
  '/audit': AuditorDashboard,
  '/audit/compliance': ComplianceReporting,
  '/audit/findings': AuditFindings,
  '/audit/findingmanagement': AuditFindingsManagement,
  '/audit/reports': ComplianceReports,
  '/billing': UnifiedBilling,

  // Portal Routes
  '/portal': UnifiedPortalDashboard,
  '/portal/dashboard': UnifiedPortalDashboard,
  '/portal/home': UnifiedPortalDashboard,
  '/portal/index': UnifiedPortalDashboard,
  '/portal/knowledgebase': UnifiedKnowledgeBase,
  '/portal/knowledge': UnifiedKnowledgeBase,
  '/portal/knowledge/articles': UnifiedKnowledgeBase,
  '/portal/knowledge/faqs': UnifiedKnowledgeBase,
  '/portal/knowledge/search': UnifiedKnowledgeBase,
  '/portal/knowledge/user-guide': UserGuide,
  '/portal/tickets': MyTickets,
  '/portal/tickets/my-tickets': MyTickets,
  '/portal/tickets/create': CreateTicketForm,
  '/portal/tickets/new': CreateTicketForm,
  '/portal/tickets/:id': TicketCommunication,
  '/portal/tickets/attachments': AttachFiles,
  '/portal/tickets/feedback': RateExperience,
  '/portal/profile': UnifiedProfile,
  '/portal/profile/preferences': UnifiedProfile,
  '/portal/profile/notifications': UnifiedProfile,
  '/portal/profile/personal': UnifiedProfile,
  '/portal/profile/activity': UnifiedProfile,
  '/portal/notifications': UnifiedProfile,
  '/portal/feedback': UnifiedFeedback,
  '/portal/feedback/collection': UnifiedFeedback,
  // Customer Chatbot
  '/portal/chatbot': LandingChatbot,

  // Developer Routes
  // '/developer': UnifiedDeveloperDashboard,
  '/developerdashboard': UnifiedDeveloperDashboard,
  '/integration-dashboard': UnifiedDeveloperDashboard,
  '/api/keys': APIKeyManagement,
  '/api/docs': APIDocumentation,
  '/api/limits': APILimits,
  '/webhooks': WebhookConfiguration,
  '/external-systems': ExternalSystemIntegration,
  '/analytics': IntegrationAnalytics,
  '/integrationsettings': IntegrationSettings,
  '/tools': DeveloperTools,
  '/profile': AgentProfileManagement,
};

/**
 * Get component by route path
 * @param {string} routePath - The route path
 * @returns {React.Component|null} - The component or null if not found
 */
export const getComponentByRoutePath = (routePath) => {
  if (!routePath) return null;

  // Normalize route path
  let normalizedPath = routePath;
  
  // Handle routes with or without /super-admin prefix
  if (!normalizedPath.startsWith('/super-admin')) {
    if (normalizedPath.startsWith('/audit') || 
        normalizedPath.startsWith('/ai') || 
        normalizedPath.startsWith('/billing')) {
      const withPrefix = '/super-admin' + normalizedPath;
      if (CENTRALIZED_ROUTE_MAP[withPrefix]) {
        return CENTRALIZED_ROUTE_MAP[withPrefix];
      }
    }
  } else {
    const withoutPrefix = normalizedPath.replace('/super-admin', '');
    if (CENTRALIZED_ROUTE_MAP[withoutPrefix]) {
      return CENTRALIZED_ROUTE_MAP[withoutPrefix];
    }
  }

  // Exact match - MUST be checked first to prioritize specific routes over parent routes
  if (CENTRALIZED_ROUTE_MAP[routePath]) {
    return CENTRALIZED_ROUTE_MAP[routePath];
  }
  if (CENTRALIZED_ROUTE_MAP[normalizedPath]) {
    return CENTRALIZED_ROUTE_MAP[normalizedPath];
  }

  // First, check dynamic routes (routes with :id, :slug, etc.)
  for (const [route, component] of Object.entries(CENTRALIZED_ROUTE_MAP)) {
    if (route.includes(':')) {
      // Dynamic route pattern matching
      const routePattern = route.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${routePattern}$`);
      if (regex.test(routePath) || regex.test(normalizedPath)) {
        return component;
      }
    }
  }

  // Then check static routes with startsWith (only if no exact or dynamic route matched)
  // IMPORTANT: Sort routes by length (longest first) to match specific routes before parent routes
  const staticRoutes = Object.entries(CENTRALIZED_ROUTE_MAP)
    .filter(([route]) => !route.includes(':'))
    .sort(([a], [b]) => b.length - a.length); // Sort by length descending (longest first)
  
  for (const [route, component] of staticRoutes) {
    // Only match if routePath exactly starts with route and is followed by / or end
    // But prioritize longer/more specific routes first
    if (routePath === route) {
      return component;
    }
    if (routePath.startsWith(route + '/') || routePath.startsWith(route + '?')) {
      return component;
    }
  }

  return null;
};

/**
 * Get all routes for a specific role prefix
 * @param {string} rolePrefix - Role prefix (e.g., '/admin', '/agent', '/super-admin')
 * @returns {Object} - Object with route paths as keys and components as values
 */
export const getRoutesByRole = (rolePrefix) => {
  const routes = {};
  for (const [route, component] of Object.entries(CENTRALIZED_ROUTE_MAP)) {
    if (route.startsWith(rolePrefix)) {
      routes[route] = component;
    }
  }
  return routes;
};

/**
 * Get all available routes
 * @returns {Object} - All routes
 */
export const getAllRoutes = () => {
  return CENTRALIZED_ROUTE_MAP;
};

export default CENTRALIZED_ROUTE_MAP;
